import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import storageManager from '../utils/StorageManager';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const APP_VERSION = '0.1.0';

function Settings() {
    const navigate = useNavigate();
    const { theme, currentTheme, setTheme } = useTheme();
    const { user, isLoading: authLoading, logout } = useAuth();
    const [playerName, setPlayerName] = useState(() => storageManager.getPlayerName());
    const [selectedTheme, setSelectedTheme] = useState(currentTheme);
    const [storageInfo, setStorageInfo] = useState(null);

    const refreshStorageInfo = () => {
        setStorageInfo(storageManager.getStorageInfo());
    };

    useEffect(() => {
        setSelectedTheme(currentTheme);
        refreshStorageInfo();
    }, [currentTheme]);

    const handleSaveSettings = () => {
        const trimmedName = playerName.trim() || 'Player 1';

        storageManager.savePlayerName(trimmedName);
        setPlayerName(trimmedName);
        setTheme(selectedTheme);
        refreshStorageInfo();
        alert('Settings saved!');
    };

    const handleClearData = () => {
        if (window.confirm('Clear ALL saved games and settings?')) {
            storageManager.clearAll();
            setTheme('dark');
            setSelectedTheme('dark');
            setPlayerName('Player 1');
            refreshStorageInfo();
            alert('All data cleared!');
        }
    };

    const handleExportData = () => {
        const data = storageManager.exportData();
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gamenight-backup-${Date.now()}.json`;
        a.click();
    };

    const handleImportData = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const success = storageManager.importData(event.target.result);
            if (success) {
                const restoredTheme = storageManager.getTheme();
                const restoredPlayerName = storageManager.getPlayerName();

                setTheme(restoredTheme);
                setSelectedTheme(restoredTheme);
                setPlayerName(restoredPlayerName);
                refreshStorageInfo();
                alert('Data imported successfully!');
            } else {
                alert('Failed to import data!');
            }
        };
        reader.readAsText(file);
    };

    const isMobile = window.innerWidth <= 768;

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: isMobile ? '16px' : '24px'
        }}>
            <BackButton to="/" />

            <div style={{
                maxWidth: '900px',
                margin: '60px auto 0',
                display: 'grid',
                gap: '16px'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <h1 style={{ marginBottom: '8px' }}>Settings</h1>
                    <p style={{ margin: 0, color: theme.textSecondary }}>
                        Manage theme, default player name, and local app data.
                    </p>
                </div>

                <section style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px'
                }}>
                    <h2 style={{ marginTop: 0 }}>Account</h2>
                    <div style={{ display: 'grid', gap: '12px' }}>
                        {authLoading ? (
                            <div style={{ color: theme.textSecondary }}>Loading account...</div>
                        ) : user ? (
                            <>
                                <div style={{ color: theme.textSecondary }}>Signed in as {user.alias || user.handle}</div>
                                <div style={{ color: theme.textSecondary }}>Handle: {user.handle}</div>
                                <button
                                    onClick={async () => {
                                        await logout();
                                        navigate('/login');
                                    }}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: theme.danger,
                                        color: theme.textPrimary,
                                        fontFamily: 'inherit',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Sign Out
                                </button>
                            </>
                        ) : (
                            <div style={{ display: 'grid', gap: '8px' }}>
                                <div style={{ color: theme.textSecondary }}>Not signed in.</div>
                                <button
                                    onClick={() => navigate('/login')}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: theme.accent,
                                        color: theme.textPrimary,
                                        fontFamily: 'inherit',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Sign In
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: theme.secondary,
                                        color: theme.textPrimary,
                                        fontFamily: 'inherit',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Create Account
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                <section style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px'
                }}>
                    <h2 style={{ marginTop: 0 }}>General</h2>

                    <div style={{ display: 'grid', gap: '14px' }}>
                        <label style={{ display: 'grid', gap: '8px' }}>
                            <span style={{ fontWeight: 'bold' }}>Default Player Name</span>
                            <input
                                value={playerName}
                                onChange={(e) => setPlayerName(e.target.value)}
                                placeholder="Player 1"
                                style={{
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: `2px solid ${theme.secondary}`,
                                    backgroundColor: theme.bgMedium,
                                    color: theme.textPrimary,
                                    fontFamily: 'inherit'
                                }}
                            />
                        </label>

                        <label style={{ display: 'grid', gap: '8px' }}>
                            <span style={{ fontWeight: 'bold' }}>Theme</span>
                            <select
                                value={selectedTheme}
                                onChange={(e) => setSelectedTheme(e.target.value)}
                                style={{
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: `2px solid ${theme.secondary}`,
                                    backgroundColor: theme.bgMedium,
                                    color: theme.textPrimary,
                                    fontFamily: 'inherit'
                                }}
                            >
                                <option value="dark">Dark</option>
                                <option value="light">Light</option>
                            </select>
                        </label>

                        <button
                            onClick={handleSaveSettings}
                            style={{
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: theme.accent,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Save Settings
                        </button>
                    </div>
                </section>

                <section style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px'
                }}>
                    <h2 style={{ marginTop: 0 }}>Storage</h2>
                    {storageInfo && (
                        <div style={{ color: theme.textSecondary, lineHeight: 1.7 }}>
                            <div>Total Items: {storageInfo.itemCount}</div>
                            <div>Storage Used: {storageInfo.sizeMB} MB ({storageInfo.percentUsed})</div>
                            <div>Estimated Limit: {storageInfo.estimatedLimit}</div>
                        </div>
                    )}
                </section>

                <section style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px'
                }}>
                    <h2 style={{ marginTop: 0 }}>Data Management</h2>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <button
                            onClick={handleExportData}
                            style={{
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: theme.secondary,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Export All Data
                        </button>

                        <label style={{ display: 'grid', gap: '8px' }}>
                            <span style={{ fontWeight: 'bold' }}>Import Data</span>
                            <input
                                type="file"
                                accept=".json"
                                onChange={handleImportData}
                                style={{ color: theme.textPrimary }}
                            />
                        </label>

                        <button
                            onClick={handleClearData}
                            style={{
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: theme.danger,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Clear All Cache
                        </button>
                    </div>
                </section>

                <section style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '24px'
                }}>
                    <h2 style={{ marginTop: 0 }}>About</h2>
                    <div style={{ color: theme.textSecondary, lineHeight: 1.7 }}>
                        <div>Game Night App</div>
                        <div>Version: {APP_VERSION}</div>
                        <div>Platform: React single-page app</div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Settings;
